import * as React from 'react';

import { cn } from '@/shadcn-ui/utils';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shadcn-ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/shadcn-ui/drawer';
import { useBreakpoint } from '../hooks/use-breakpoint';
import Spacer from './spacer';
import { CancelCircleProps } from '@/icons/cancel-with-circle';

/**
 * Base properties shared by both DirectControlProps and TriggerControlProps.
 */
interface DrawerDialogBaseProps {
  title?: string | React.ReactNode;
  children?: React.ReactNode;
  /**
   * Whether the Drawer/Dialog should be dismissible by clicking outside of it.
   */
  dismissible?: boolean;
  /**
   * The variant of the Drawer/Dialog.
   * - `default`: The title is at left-aligned at the top of the Drawer/Dialog.
   * - `center-title`: The title is centered at the top of the Drawer/Dialog. Header and footter are sticky and content scrolls.
   *
   * Note: The `center-title` variant is specifically designed for the FilterMenu component at this time.
   * We have custom bottom padding to account for the generous clickbox for filter checkboxes.
   * If you other dialogs need to use this variant, accont for this during refactoring.
   */
  variant?: 'default' | 'center-title';
  /**
   * Footer content to be displayed at the bottom of the Drawer/Dialog.
   * Only applicable when `variant` is set to 'center-title', if you want to use it with 'default' variant, you need to add it inside children.
   */
  footer?: React.ReactNode;
  /**
   * Whether to remove the padding from the content of the Drawer/Dialog.
   *
   * If you plan on using this prop, please makes sure your modal content
   * has it's own padding of 24px to match our design system. Title paddding is
   * also removed here, so more specifically, be sure to wrap the entire content
   * block in a div with "p-6" to maintain the correct title-content gap.
   *
   * @example
   * <DrawerDialog
   *   title="Cool drawer"
   *   trigger={trigger}
   *   removeContentPadding={true}
   * >
   *   <div className="p-6">
   *     <Button>Click me!</Button>
   *   </div>
   * </DrawerDialog>
   */
  removeContentPadding?: boolean;
  /** Classes meant to style header DOM element */
  headerClassName?: string;
  /** Classes meant to style children content DOM element passed to modal */
  childrenContainerClassName?: string;
  /** Classes meant to style content DOM element */
  contentClassName?: string;
  closeIconPropConfiguration?: CancelCircleProps;
  /** If false we do not move the modal for software keyboard opening. If you are using an input in your drawer it is recommended to set this to false otherwise you will have unpredictable behavior. */
  repositionInputs?: boolean;
  /**
   * Function that is called when the Drawer/Dialog is opened or closed
   */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Properties for when the Drawer/Dialog is directly controlled by the parent component.
 * In this mode, `trigger` should not be provided, as it's open state is managed by the
 * `open` and `handleClose` props.
 */
interface DirectControlProps extends DrawerDialogBaseProps {
  /**
   * Whether the Drawer/Dialog is open or not.
   */
  open: boolean;
  /**
   * Function to close the Drawer/Dialog.
   */
  handleClose: () => void;
  /**
   * `trigger` should not be passed in this mode of operation.
   */
  trigger?: never;
}

/**
 * Properties for when the Drawer/Dialog is controlled by a trigger component.
 * In this mode, `open` and `handleClose` should not be provided as it's
 * open state is internally managed.
 */
interface TriggerControlProps extends DrawerDialogBaseProps {
  /**
   * When composing a trigger, it must follow Radix UI's guidelines or else it will not be functional.
   * @see https://www.radix-ui.com/primitives/docs/guides/composition#composing-with-your-own-react-components
   */
  trigger: React.ReactNode;
  /**
   * `open` should not be passed in this mode of operation.
   */
  open?: never;
  /**
   * `handleClose` should not be passed in this mode of operation.
   */
  handleClose?: never;
}

export type DrawerDialogFullProps = DirectControlProps | TriggerControlProps;

/**
 * Properties to be passed to a `DrawerDialog` component when controlled directly.
 *
 * @example
 * import { DrawerDialog, DrawerDialogProps } from '@/components/drawer-dialog';
 *
 * interface MyModalProps extends DrawerDialogProps {}
 *
 * export default function MyModal({ open, handleClose }: MyModalProps) {
 *   return (
 *     <DrawerDialog title="Super cool modal" open={open} handleClose={handleClose}>
 *       // Your dialog content here
 *     </DrawerDialog>
 *   );
 * }
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface DrawerDialogProps
  extends Pick<DirectControlProps, 'open' | 'handleClose' | 'dismissible'> {}

/**
 * The `DrawerDialog` component is an abstraction from the shadcn/ui's demo component.
 * It renders a `Dialog` component on desktop and a `Drawer` component on mobile.
 *
 * This component can operate in two modes:
 * - Direct control mode: Requires `open` and `handleClose` props to control its visibility.
 * - Trigger mode: Utilizes a `trigger` component which will open the Drawer/Dialog on click.
 *
 * @see https://ui.shadcn.com/docs/components/drawer#usage
 */
export function DrawerDialog({
  open,
  handleClose,
  trigger,
  title,
  children,
  dismissible = true,
  variant = 'default',
  footer,
  removeContentPadding,
  headerClassName,
  childrenContainerClassName,
  contentClassName,
  closeIconPropConfiguration,
  repositionInputs,
  onOpenChange: _onOpenChange,
}: DrawerDialogFullProps) {
  const breakpoint = useBreakpoint();
  const isDesktop = breakpoint !== 'mobile';

  const onOpenChange = (open: boolean) => {
    if (_onOpenChange) _onOpenChange(open);
    if (!dismissible) return;
    if (!open && handleClose) handleClose();
  };

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}

        <DialogContent
          // TODO: adjust scrolling layout so that entire modal/drawer scroll, not just inner contents (RUBY-272)
          className={cn(
            'sm:max-w-[600px]',
            {
              'p-0': removeContentPadding || variant === 'center-title',
              'flex flex-col': variant === 'center-title',
            },
            contentClassName
          )}
          closeIconPropConfiguration={closeIconPropConfiguration}
          dismissable={dismissible}
        >
          {title ? (
            <DialogHeader
              className={cn(
                {
                  // We assume that the content padding of p-6 will account for the pb-6 title padding removed here
                  'px-6 pt-6': removeContentPadding,
                  // For center-title, add a bottom border for the sticky header
                  'mt-0 border-b-[1px] border-flexcar-neutral80 px-6 pb-[20px] pt-[22px] text-center':
                    variant === 'center-title',
                },
                headerClassName
              )}
            >
              <DialogTitle
                className={cn(
                  'font-sans text-[28px] font-semibold leading-[34px] tracking-[-0.005em]',
                  {
                    'text-base': variant === 'center-title',
                  }
                )}
              >
                {title}
              </DialogTitle>
            </DialogHeader>
          ) : null}

          {variant === 'center-title' ? (
            <div
              className={cn('flex-[1_1_0%] px-6 pb-4 pt-6', childrenContainerClassName)}
            >
              {children}
            </div>
          ) : (
            <>
              {!removeContentPadding ? <Spacer variant="height16" /> : null}
              {children}
            </>
          )}

          {variant === 'center-title' && footer ? (
            // For center-title, add a top border for the sticky footer
            <DialogFooter className="border-t-[1px] border-flexcar-neutral80 px-6">
              {footer}
            </DialogFooter>
          ) : null}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      dismissible={dismissible}
      repositionInputs={repositionInputs}
    >
      {trigger ? <DrawerTrigger asChild>{trigger}</DrawerTrigger> : null}

      <DrawerContent
        // TODO: adjust scrolling layout so that entire modal/drawer scroll, not just inner contents (RUBY-272)
        showCloseIcon={dismissible}
        closeIconPropConfiguration={closeIconPropConfiguration}
        removeContentPadding={removeContentPadding || variant === 'center-title'}
        className={cn(contentClassName)}
      >
        {title ? (
          <DrawerHeader
            className={cn(
              'text-left',
              {
                // We assume that the content padding of p-6 will account for the pb-6 title padding removed here
                'px-6 pt-6': removeContentPadding,
                // For center-title, add a bottom border for the sticky header
                'mt-0 border-b-[1px] border-flexcar-neutral80 px-6 pb-[20px] pt-[22px] text-center':
                  variant === 'center-title',
              },
              headerClassName
            )}
          >
            <DrawerTitle
              className={cn('font-sans font-semibold leading-7 tracking-[-0.005em]', {
                'text-center text-base': variant === 'center-title',
              })}
            >
              {title}
            </DrawerTitle>
          </DrawerHeader>
        ) : null}

        {variant === 'center-title' ? (
          <div
            className={cn(
              'scrollbar-hide flex-[1_1_0%] px-6 pb-4 pt-6',
              childrenContainerClassName
            )}
          >
            {children}
          </div>
        ) : (
          <>
            {!removeContentPadding ? <Spacer variant="height16" /> : null}
            {children}
          </>
        )}

        {variant === 'center-title' && footer ? (
          // For center-title, add a top border for the sticky footer
          <DialogFooter className="border-t-[1px] border-flexcar-neutral80 px-6">
            {footer}
          </DialogFooter>
        ) : null}
      </DrawerContent>
    </Drawer>
  );
}
